import React, { useState } from 'react';
import { Download, Loader2, CheckCircle2, AlertTriangle, XCircle, Info } from 'lucide-react';
import { stockApi, downloadAuthed } from '../api';

const TONE = {
  success: { cls: 'bg-[#E6F2E9] text-[#2f6b41] border-[#cfe5d6]', Icon: CheckCircle2, title: 'All rows imported' },
  partial: { cls: 'bg-[#FDF3E1] text-[#8a5a12] border-[#f1dfb8]', Icon: AlertTriangle, title: 'Imported with some errors' },
  failed: { cls: 'bg-[#FBE7E4] text-[#9b2c1f] border-[#f1cfc9]', Icon: XCircle, title: 'Nothing was imported' },
  skipped: { cls: 'bg-[#E8EEF8] text-[#2c4a7a] border-[#d3dff0]', Icon: Info, title: 'Already imported — nothing new' },
};

const Stat = ({ label, value, testId }) => (
  <div className="px-3 py-2 rounded-md bg-white/70 min-w-[110px]">
    <div className="text-[11.5px] opacity-80">{label}</div>
    <div className="text-[20px] font-semibold leading-tight" data-testid={testId}>{value ?? '—'}</div>
  </div>
);

export const downloadStockReport = (id, name) => downloadAuthed(stockApi.reportUrl(id), name || `stock-import-report_${id}.xlsx`);

export default function StockImportSummary({ result }) {
  const [busy, setBusy] = useState(false);
  const status = result.status || (result.ok ? 'success' : 'failed');
  const { cls, Icon, title } = TONE[status] || TONE.failed;
  const errs = result.errors || [];
  const download = async () => {
    setBusy(true);
    try { await downloadStockReport(result.reportId, result.reportFileName); } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="stock-import-result" className={`mb-4 px-4 py-3 rounded-md border text-[13px] ${cls}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Icon size={18} />
        <div className="font-medium" data-testid="stock-import-status">{title}{result.kind === 'sync' ? ' (ERP email)' : ''}</div>
        {result.reportId && result.reportAvailable !== false && (
          <button data-testid="stock-import-report-download" onClick={download} disabled={busy}
            className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white border border-current/20 text-[12.5px] hover:bg-white/80 disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download report (Excel)
          </button>
        )}
      </div>
      {result.reportId && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Stat label="Rows in sheet" value={result.total} testId="stock-import-total" />
          <Stat label="Imported" value={result.written} testId="stock-import-written" />
          <Stat label="Errors" value={result.failed ?? errs.length} testId="stock-import-failed" />
        </div>
      )}
      {errs.length > 0 && (
        <ul data-testid="stock-import-errors" className="mt-3 space-y-0.5 max-h-[200px] overflow-auto text-[12.5px]">
          {errs.slice(0, 50).map((e, i) => <li key={i}>{e.row ? `Row ${e.row}: ` : ''}{e.reason}</li>)}
          {errs.length > 50 && <li className="opacity-80">…and {errs.length - 50} more — see the Errors tab in the report.</li>}
        </ul>
      )}
    </div>
  );
}
